import React, { useEffect, useState } from "react";
import { Link2, Search, RotateCcw, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown ";

interface TcpConnection {
    id: number;
    state: string;
    local_address: string;
    local_port: number;
    remote_address: string;
    remote_port: number;
}

const TcpConnections: React.FC = () => {
    const [data, setData] = useState<TcpConnection[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination state
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;

    const fetchData = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * itemsPerPage;
            const limit = itemsPerPage;

            const queryObj: any = { asset_id: asset_id };
            if (debouncedSearchTerm) {
                queryObj["$or"] = [
                    { local_address: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { remote_address: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { state: { "$ilike": `%${debouncedSearchTerm}%` } }
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip,
                limit,
            };

            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/tcp_connections", { params });
            if (response.data.status) {
                setData(response.data.data || []);
                setTotalItems(response.data.total || 0);
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        fetchData();
    }, [currentPage, itemsPerPage, sortConfig, debouncedSearchTerm]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);



    const getStatusColor = (state: string) => {
        switch (state.toLowerCase()) {
            case 'listen': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
            case 'established': return 'bg-green-500/10 text-green-500 border-green-500/20';
            case 'closed': return 'bg-red-500/10 text-red-500 border-red-500/20';
            case 'time_wait': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
            default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
        }
    };

    return (
        <div className="space-y-4 animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <Link2 className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-sm font-bold text-white leading-tight">TCP Connections</h2>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search connections..."
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-orange-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={() => fetchData()}
                        className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white"
                    >
                        <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-900/50 border-b border-gray-700">
                                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer whitespace-nowrap" onClick={() => handleSort('state')}>
                                    <div className="flex items-center gap-1">State {sortConfig?.key === 'state' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</div>
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer whitespace-nowrap" onClick={() => handleSort('local_address')}>
                                    <div className="flex items-center gap-1">Local Address {sortConfig?.key === 'local_address' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</div>
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer whitespace-nowrap" onClick={() => handleSort('local_port')}>
                                    <div className="flex items-center gap-1">Local Port {sortConfig?.key === 'local_port' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</div>
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer whitespace-nowrap" onClick={() => handleSort('remote_address')}>
                                    <div className="flex items-center gap-1">Remote Address {sortConfig?.key === 'remote_address' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</div>
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer whitespace-nowrap" onClick={() => handleSort('remote_port')}>
                                    <div className="flex items-center gap-1">Remote Port {sortConfig?.key === 'remote_port' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="py-20 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <RotateCcw className="w-8 h-8 text-orange-500 animate-spin" />
                                            <span className="text-gray-400">Loading TCP connections...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-20 text-center text-gray-500">
                                        No connections found
                                    </td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-700/30 transition-colors">
                                        <td className="px-4 py-3 text-sm whitespace-nowrap font-medium">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${getStatusColor(item.state)}`}>
                                                {item.state}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-300 font-mono whitespace-nowrap">{item.local_address}</td>
                                        <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap font-bold text-orange-400">{item.local_port}</td>
                                        <td className="px-4 py-3 text-sm text-gray-300 font-mono whitespace-nowrap">{item.remote_address}</td>
                                        <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap font-bold text-blue-400">{item.remote_port}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="bg-gray-900/40 px-4 py-2 border-t border-gray-700">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">Show</span>
                                <ItemsPerPageDropdown
                                    value={itemsPerPage}
                                    onChange={(val) => {
                                        setItemsPerPage(val);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                            <span className="text-xs text-gray-400">
                                Total: {totalItems} connections
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                <ChevronLeft className="w-4 h-4 -ml-2" />
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <div className="bg-orange-600/20 text-orange-500 w-7 h-7 flex items-center justify-center rounded text-xs font-bold border border-orange-500/30">
                                {currentPage}
                            </div>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <ChevronRight className="w-4 h-4" />
                                <ChevronRight className="w-4 h-4 -ml-2" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TcpConnections;
